/**
 * Skill Demand Chart Component
 * Visualizes skill demand, growth, and salary data
 */

import React, { useState, useEffect } from 'react';
import careerService from '../../services/careerService';
import './SkillDemandChart.css';

const SkillDemandChart = ({ skill, region = 'global', onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(region);

  useEffect(() => {
    loadSkillDemand();
  }, [skill, selectedRegion]);

  const loadSkillDemand = async () => {
    try {
      setLoading(true);
      const demandData = await careerService.getSkillDemand(skill, selectedRegion);
      setData(demandData);
      setError(null);
    } catch (err) {
      console.error('Error loading skill demand:', err);
      setError('Failed to load skill demand data');
    } finally {
      setLoading(false);
    }
  };

  const getDemandLevel = (score) => {
    if (score >= 80) return { label: 'Very High', color: '#4CAF50' };
    if (score >= 60) return { label: 'High', color: '#8BC34A' };
    if (score >= 40) return { label: 'Medium', color: '#FFC107' };
    if (score >= 20) return { label: 'Low', color: '#FF9800' };
    return { label: 'Very Low', color: '#f44336' };
  };

  const getGrowthIcon = (rate) => {
    if (rate > 10) return { icon: 'fa-arrow-trend-up', color: '#4CAF50', label: 'Growing' };
    if (rate > 0) return { icon: 'fa-arrow-up', color: '#8BC34A', label: 'Rising' };
    if (rate > -10) return { icon: 'fa-minus', color: '#FFC107', label: 'Stable' };
    return { icon: 'fa-arrow-down', color: '#f44336', label: 'Declining' };
  };

  const formatSalary = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data?.salary?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="skill-demand-chart">
        <div className="chart-loading">
          <div className="spinner"></div>
          <p>Analyzing {skill}...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="skill-demand-chart">
        <div className="chart-error">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  const demandLevel = getDemandLevel(data.demandScore);
  const growthInfo = getGrowthIcon(data.growthRate);

  return (
    <div className="skill-demand-chart">
      {/* Header */}
      <div className="chart-header">
        <div className="header-left">
          <h2>
            <i className="fas fa-chart-line"></i>
            {skill} - Market Analysis
          </h2>
          <p className="region-label">
            <i className="fas fa-globe"></i>
            {selectedRegion === 'global' ? 'Global' : selectedRegion}
          </p>
        </div>
        <div className="header-right">
          <select 
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="region-select"
          >
            <option value="global">Global</option>
            <option value="Africa">Africa</option>
            <option value="Europe">Europe</option>
            <option value="Americas">Americas</option>
            <option value="Asia">Asia</option>
          </select>
          {onClose && (
            <button onClick={onClose} className="close-btn">
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      {/* Main Metrics */}
      <div className="metrics-grid">
        {/* Demand Score Gauge */}
        <div className="metric-card demand-gauge">
          <h3>Demand Score</h3>
          <div className="gauge-container">
            <svg viewBox="0 0 200 120" className="gauge">
              {/* Background arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="#e0e0e0"
                strokeWidth="20"
                strokeLinecap="round"
              />
              {/* Filled arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke={demandLevel.color}
                strokeWidth="20"
                strokeLinecap="round"
                strokeDasharray={`${(data.demandScore / 100) * 251.2} 251.2`}
                style={{ transition: 'stroke-dasharray 1s ease' }}
              />
            </svg>
            <div className="gauge-value">
              <span className="score">{data.demandScore}</span>
              <span className="max">/100</span>
            </div>
            <div className="gauge-label" style={{ color: demandLevel.color }}>
              {demandLevel.label}
            </div>
          </div>
        </div>

        {/* Growth Rate */}
        <div className="metric-card growth-rate">
          <h3>Growth Rate</h3>
          <div className="growth-display">
            <i 
              className={`fas ${growthInfo.icon}`}
              style={{ color: growthInfo.color }}
            ></i>
            <div className="growth-value">
              <span className="rate" style={{ color: growthInfo.color }}>
                {data.growthRate > 0 ? '+' : ''}{data.growthRate.toFixed(1)}%
              </span>
              <span className="period">Last 30 days</span>
            </div>
          </div>
          <div className="growth-label" style={{ color: growthInfo.color }}>
            {growthInfo.label}
          </div>
        </div>

        {/* Job Count */}
        <div className="metric-card job-count">
          <h3>Active Jobs</h3>
          <div className="count-display">
            <i className="fas fa-briefcase"></i>
            <span className="count">{data.jobCount?.toLocaleString()}</span>
          </div>
          <p className="count-label">Current openings</p>
        </div>
      </div>

      {/* Salary Information */}
      {data.salary && data.salary.disclosed && (
        <div className="salary-section">
          <h3>
            <i className="fas fa-dollar-sign"></i>
            Salary Range
          </h3>
          <div className="salary-bars">
            <div className="salary-bar">
              <div className="bar-label">Minimum</div>
              <div className="bar-container">
                <div 
                  className="bar-fill min"
                  style={{ width: `${(data.salary.min / data.salary.max) * 100}%` }}
                ></div>
                <span className="bar-value">{formatSalary(data.salary.min)}</span>
              </div>
            </div>
            <div className="salary-bar">
              <div className="bar-label">Average</div>
              <div className="bar-container">
                <div 
                  className="bar-fill avg"
                  style={{ width: `${(data.salary.average / data.salary.max) * 100}%` }}
                ></div>
                <span className="bar-value">{formatSalary(data.salary.average)}</span>
              </div>
            </div>
            <div className="salary-bar">
              <div className="bar-label">Maximum</div>
              <div className="bar-container">
                <div className="bar-fill max" style={{ width: '100%' }}></div>
                <span className="bar-value">{formatSalary(data.salary.max)}</span>
              </div>
            </div>
          </div>
          <p className="salary-note">
            Based on {data.salary.count} disclosed salaries • {data.salary.period}
          </p>
        </div>
      )}

      {/* Top Companies */}
      {data.topCompanies && data.topCompanies.length > 0 && (
        <div className="companies-section">
          <h3>
            <i className="fas fa-building"></i>
            Top Hiring Companies
          </h3>
          <div className="companies-grid">
            {data.topCompanies.slice(0, 6).map((company, index) => (
              <div key={index} className="company-card">
                {company.logo && (
                  <img src={company.logo} alt={company.name} className="company-logo" />
                )}
                <div className="company-info">
                  <h4>{company.name}</h4>
                  <p className="company-industry">{company.industry}</p>
                  <span className="company-openings">
                    {company.openings} {company.openings === 1 ? 'opening' : 'openings'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Skills */}
      {data.relatedSkills && data.relatedSkills.length > 0 && (
        <div className="related-skills-section">
          <h3>
            <i className="fas fa-link"></i>
            Related Skills
          </h3>
          <div className="skills-tags">
            {data.relatedSkills.slice(0, 10).map((relatedSkill, index) => (
              <div 
                key={index} 
                className={`skill-tag ${relatedSkill.importance}`}
              >
                <span className="skill-name">{relatedSkill.name}</span>
                <span className="skill-count">
                  {relatedSkill.count} {relatedSkill.count === 1 ? 'job' : 'jobs'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Experience Level Breakdown */}
      {data.experienceLevels && (
        <div className="experience-section">
          <h3>
            <i className="fas fa-layer-group"></i>
            Jobs by Experience Level
          </h3>
          <div className="experience-bars">
            {Object.entries(data.experienceLevels).map(([level, count]) => (
              <div key={level} className="experience-bar">
                <div className="level-label">{level.charAt(0).toUpperCase() + level.slice(1)}</div>
                <div className="level-container">
                  <div 
                    className="level-fill"
                    style={{ width: `${(count / data.jobCount) * 100}%` }}
                  ></div>
                  <span className="level-count">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillDemandChart;
